import React from 'react';
import { Package, TrendingUp, IndianRupee, BarChart3, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '../../config';
import { SummaryCard, MetricCard } from './SharedCards';
import { deltaForKey } from './trendUtils';

// Months in Indian Fiscal Year order (Apr → Mar).
const FY_MONTH_NAMES = ['Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar'];

const VOLUME_EFF_ITEMS = [
  ['Steam/MT', 'steam_mt_per_mt', 'MT', 'steam'],
  ['Water/MT', 'water_kl_per_mt', 'KL', 'water'],
  ['Oil/MT', 'oil_l_per_mt', 'L', 'oil'],
  ['Carbon/MT', 'carbon_kgs_per_mt', 'Kg', 'carbon'],
  ['Alumina/MT', 'alumina_kgs_per_mt', 'Kg', 'alumina'],
  ['13X Mol Sieves/MT', 'mol_sieves_kgs_per_mt', 'Kg', 'mol_sieves'],
  ['NH3/MT', 'nh3_kgs_per_mt', 'Kg', 'nh3'],
  ['KMNO4/MT', 'kmno4_kgs_per_mt', 'Kg', 'kmno4'],
];

const COST_BREAKDOWN_ITEMS = [
  ['Power', 'power_cost_per_mt', 'power'],
  ['Steam', 'steam_cost_per_mt', 'steam'],
  ['Water', 'water_cost_per_mt', 'water'],
  ['Oil', 'oil_cost_per_mt', 'oil'],
  ['Carbon', 'carbon_cost_per_mt', 'carbon'],
  ['Alumina', 'alumina_cost_per_mt', 'alumina'],
  ['13X Mol Sieves', 'mol_sieves_cost_per_mt', 'mol_sieves'],
  ['NH3', 'nh3_cost_per_mt', 'nh3'],
  ['KMNO4', 'kmno4_cost_per_mt', 'kmno4'],
  ['Maintenance', 'maintenance_cost_per_mt', 'maintenance'],
  ['Food Grade', 'food_grade_cost_per_mt', 'food_grade'],
];

const YtdDataView = ({ ytdData, trendData, stoppageMins = 0, onStoppageClick, openSummaryDrillDown, openDrillDown }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
      <SummaryCard title="YTD Production" value={`${ytdData.ytd_production_mt} MT`} color="blue" icon={<Package />} summaryKey="production" onClick={openSummaryDrillDown} delta={deltaForKey(trendData, 'production')} />
      <SummaryCard title="YTD Despatches" value={`${ytdData.ytd_despatches_mt} MT`} color="green" icon={<TrendingUp />} summaryKey="despatches" onClick={openSummaryDrillDown} delta={deltaForKey(trendData, 'despatches')} />
      <SummaryCard title="YTD Cost" value={formatCurrency(ytdData.ytd_cost)} color="orange" icon={<IndianRupee />} summaryKey="cost" onClick={openSummaryDrillDown} delta={deltaForKey(trendData, 'cost')} deltaInvert />
      <SummaryCard title="Cost/MT" value={formatCurrency(ytdData.cost_efficiency?.total_cost_per_mt || 0)} color="purple" icon={<BarChart3 />} summaryKey="cost_per_mt" onClick={openSummaryDrillDown} delta={deltaForKey(trendData, 'cost_per_mt')} deltaInvert />
      <SummaryCard title="Stoppage (YTD)" value={`${Number(stoppageMins || 0).toLocaleString('en-IN')} min`} color="orange" icon={<AlertTriangle />} summaryKey="stoppage" onClick={onStoppageClick} />
    </div>

    {ytdData.volume_efficiency && (
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Volume Efficiency (per MT) - YTD</h3>
          <span className="text-xs text-[#156ACA]">Tap for monthly breakdown</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <MetricCard
            label="Power/MT"
            value={`${ytdData.volume_efficiency.power_units_per_mt} U`}
            subValue={`${ytdData.volume_efficiency.power_kgs_per_unit || 0}`}
            subLabel="kgs/Unit"
            metricKey="power"
            onClick={openDrillDown}
          />
          {VOLUME_EFF_ITEMS.map(([label, key, unit, mk]) => (
            <MetricCard key={mk} label={label} value={`${ytdData.volume_efficiency[key] || 0} ${unit}`} metricKey={mk} onClick={openDrillDown} />
          ))}
        </div>
      </div>
    )}

    {ytdData.cost_breakdown && (ytdData.cost_breakdown.co2_purchase || 0) > 0 && (
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6" data-testid="co2-purchase-panel">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">CO₂ Purchase Cost - YTD</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <MetricCard label="Base CO₂ Cost" value={formatCurrency(ytdData.cost_breakdown.co2_purchase_base || 0)} metricKey="co2_base" />
          <MetricCard label="Loss Adjustment" value={formatCurrency(ytdData.cost_breakdown.co2_loss_extra || 0)} metricKey="co2_loss_extra" />
          <div className="bg-[#EEF2FF] p-3 rounded-lg border border-[#156ACA]/30">
            <p className="text-xs text-[#156ACA] font-semibold">CO₂ Cost (Loss-Adjusted) - used in Total</p>
            <p className="text-xl font-bold text-[#156ACA]">{formatCurrency(ytdData.cost_breakdown.co2_purchase || 0)}</p>
          </div>
        </div>
      </div>
    )}

    {ytdData.cost_efficiency && (
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Cost Breakdown (Rs/MT) - YTD</h3>
          <span className="text-xs text-[#156ACA]">Tap for monthly breakdown</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
          {COST_BREAKDOWN_ITEMS.map(([label, key, mk]) => (
            <MetricCard key={mk} label={label} value={formatCurrency(ytdData.cost_efficiency[key] || 0)} metricKey={mk} onClick={openDrillDown} />
          ))}
        </div>
      </div>
    )}

    {ytdData.monthly_breakdown?.length > 0 && (
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Monthly Breakdown</h3>
        <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Production (MT)</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Despatches (MT)</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ytdData.monthly_breakdown.map(m => (
                <tr key={m.month}>
                  <td className="px-4 py-3 font-medium">{m.month_name || FY_MONTH_NAMES[[4,5,6,7,8,9,10,11,12,1,2,3].indexOf(m.month)]}</td>
                  <td className="px-4 py-3 text-right">{m.production_mt}</td>
                  <td className="px-4 py-3 text-right">{m.despatches_mt}</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(m.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
);

export default YtdDataView;
